import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowRight, MapPin, Users, Award, Target, Lightbulb, TrendingUp, ShieldCheck, ChevronDown, Globe, Zap, Building2, Clock } from 'lucide-react';
import PageLayout from '@/components/layout/PageLayout';

const values = [
  { icon: Target, key: 'precision' },
  { icon: Users, key: 'partnership' },
  { icon: Lightbulb, key: 'innovation' },
];

const industryContext = [
  { icon: TrendingUp, key: 'problem', text: 'Multinationals typically manage 10 to 30 vendor relationships for indirect tax compliance alone — each with different platforms, contracts, upgrade cycles, and levels of service quality. The cost isn\'t just financial; it\'s operational risk compounded across every jurisdiction.' },
  { icon: ShieldCheck, key: 'conviction', text: 'Technology should eliminate complexity, not profit from it. A single, cloud-native platform can replace the entire patchwork — giving tax teams full control, real-time visibility across every country, and the confidence that compliance works. Every transaction, every time.' },
  { icon: Award, key: 'advantage', text: 'Our founding team spent decades inside the Big Four and enterprise ERP vendors. We know exactly where the existing systems fail — because we helped build them. That experience is now channelled into engineering a platform that works the way multinationals actually operate.' },
];

const byNumbers = [
  { icon: Globe, text: 'Single platform for every jurisdiction' },
  { icon: Building2, text: 'Swiss-engineered in Wollerau, SZ' },
];

const AboutPage = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const [expandedValue, setExpandedValue] = useState(null);

  useEffect(() => {
    if (location.hash) {
      const el = document.getElementById(location.hash.slice(1));
      if (el) setTimeout(() => el.scrollIntoView({ behavior: 'smooth' }), 150);
    }
  }, [location.hash]);

  return (
    <PageLayout>
      {/* Hero */}
      <section id="company" className="pb-16 pt-8" data-testid="about-hero">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
              <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-taxera-mint/20 text-taxera-teal text-sm font-medium rounded-full mb-6">
                <MapPin className="w-4 h-4" strokeWidth={1.5} />
                {t('about.badge')}
              </div>
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-taxera-charcoal leading-tight mb-6">
                Enterprise Tax Compliance, <br />
                <span className="taxera-gradient-text">Engineered Differently</span>
              </h1>
              <p className="text-lg text-slate-600 leading-relaxed mb-4">
                Founded in 2019 by tax technology veterans with decades of Big Four and ERP experience, Taxera was purpose-built to solve the problems the industry has failed to address — fragmented vendors, manual processes, and compliance systems that cannot scale.
              </p>
              <p className="text-base text-slate-500 leading-relaxed mb-4">
                Taxera is a cloud-native enterprise tax compliance platform for multinationals, covering e-Invoicing, SAF-T, VAT returns, and eReconciliation across multiple jurisdictions worldwide.
              </p>
              <p className="text-base text-slate-500 leading-relaxed">
                One platform, every country, complete control. We replace complexity with clarity for the world's largest enterprises.
              </p>
            </motion.div>

            {/* Descriptors */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2 }}
              className="grid grid-cols-1 gap-4"
            >
              {byNumbers.map((item, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 + i * 0.1 }}
                  className="bg-slate-900 rounded-2xl p-6 flex items-center gap-4 group hover:bg-slate-800 transition-colors"
                  data-testid={`about-stat-${i}`}
                >
                  <item.icon className="w-8 h-8 text-taxera-teal shrink-0" strokeWidth={1.5} />
                  <div className="text-slate-300 text-base font-medium">{item.text}</div>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Industry Context — Why we exist */}
      <section id="mission" className="py-20 bg-slate-900" data-testid="about-context">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <div className="text-xs font-bold tracking-[0.2em] text-taxera-teal uppercase mb-4">Why Taxera exists</div>
            <h2 className="font-heading text-3xl lg:text-4xl font-bold text-white mb-4">
              A fundamentally different approach
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              Global indirect tax compliance is a $40 billion market — yet multinationals still juggle dozens of fragmented vendors, manual processes, and platforms that were never designed to work together. We believe they deserve better.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {industryContext.map((item, i) => {
              const Icon = item.icon;
              return (
                <motion.div
                  key={item.key}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className="bg-white/5 border border-white/10 rounded-2xl p-8 hover:bg-white/10 transition-colors"
                  data-testid={`context-card-${i}`}
                >
                  <Icon className="w-8 h-8 text-taxera-teal mb-4" strokeWidth={1.5} />
                  <h3 className="font-heading text-xl font-bold text-white mb-3">{t(`about.industryContext.${item.key}`)}</h3>
                  <p className="text-slate-400 text-sm leading-relaxed">{item.text}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* What makes us different */}
      <section id="differentiators" className="py-20 bg-white" data-testid="about-differentiators">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl lg:text-4xl font-bold text-taxera-charcoal mb-4">
              What makes Taxera different
            </h2>
            <div className="w-20 h-1 taxera-gradient mx-auto rounded-full" />
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Globe, title: 'Born global', desc: 'Built from day one for multi-country compliance. Not a local tool stretched to fit.' },
              { icon: Zap, title: 'Cloud-native', desc: 'No legacy architecture. No migration debt. Pure cloud, SAP BTP-native.' },
              { icon: Building2, title: 'Single platform', desc: 'One codebase. One UX. One contract. Not acquired parts stitched together.' },
              { icon: Clock, title: 'Speed to value', desc: '8-12 week implementations. Designed to run parallel to your projects.' },
            ].map((d, i) => {
              const Icon = d.icon;
              return (
                <motion.div
                  key={d.title}
                  initial={{ opacity: 0, y: 15 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.08 }}
                  className="text-center p-6"
                  data-testid={`differentiator-${i}`}
                >
                  <div className="w-14 h-14 rounded-2xl bg-taxera-teal/10 flex items-center justify-center mx-auto mb-4">
                    <Icon className="w-6 h-6 text-taxera-teal" strokeWidth={1.5} />
                  </div>
                  <h3 className="font-heading text-base font-bold text-taxera-charcoal mb-2">{d.title}</h3>
                  <p className="text-sm text-slate-500 leading-relaxed">{d.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Values — Interactive */}
      <section className="py-20 bg-slate-50" data-testid="about-values">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl lg:text-4xl font-bold text-taxera-charcoal mb-4">
              {t('about.valuesTitle')}
            </h2>
            <div className="w-20 h-1 taxera-gradient mx-auto rounded-full" />
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {values.map((value, i) => {
              const Icon = value.icon;
              const isExpanded = expandedValue === i;
              return (
                <motion.button
                  key={value.key}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  onClick={() => setExpandedValue(isExpanded ? null : i)}
                  className={`bg-white rounded-2xl p-8 border text-left transition-all duration-300 ${
                    isExpanded
                      ? 'border-taxera-teal/30 shadow-lg shadow-taxera-teal/5'
                      : 'border-slate-200 hover:shadow-md'
                  }`}
                  data-testid={`value-card-${i}`}
                >
                  <Icon className={`w-8 h-8 mb-4 transition-colors ${isExpanded ? 'text-taxera-blue' : 'text-taxera-teal'}`} strokeWidth={1.5} />
                  <h3 className="font-heading text-xl font-bold text-taxera-charcoal mb-3">{t(`about.values.${value.key}`)}</h3>
                  <p className="text-slate-600 leading-relaxed text-sm">{t(`about.values.${value.key}Desc`)}</p>
                  <AnimatePresence>
                    {isExpanded && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="mt-4 pt-4 border-t border-slate-100"
                      >
                        <p className="text-sm font-medium text-taxera-teal">{value.detail}</p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                  <div className={`mt-4 w-6 h-6 rounded-full flex items-center justify-center transition-all ${
                    isExpanded ? 'bg-taxera-teal/10 rotate-180' : 'bg-slate-100'
                  }`}>
                    <ChevronDown className="w-3.5 h-3.5 text-slate-400" />
                  </div>
                </motion.button>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20" data-testid="about-cta">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-heading text-3xl lg:text-4xl font-bold text-taxera-charcoal mb-4">
            Ready to talk?
          </h2>
          <p className="text-lg text-slate-600 mb-8 max-w-2xl mx-auto">
            Whether you're evaluating platforms, planning an S/4HANA migration, or ready to consolidate your compliance stack — we'd love to hear from you.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="group inline-flex items-center gap-2 px-8 py-4 taxera-gradient text-white rounded-full font-semibold hover:shadow-lg transition-all hover:scale-105"
              data-testid="about-contact-btn"
            >
              Get in Touch
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link
              to="/evaluate"
              className="inline-flex items-center gap-2 px-8 py-4 bg-white text-taxera-charcoal border border-slate-200 rounded-full font-semibold hover:bg-slate-50 transition-all"
              data-testid="about-diagnostic-btn"
            >
              Take the Diagnostic
            </Link>
          </div>
        </div>
      </section>
    </PageLayout>
  );
};

export default AboutPage;
